import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form, Input, InputNumber, Button, Divider } from 'antd';
import LicenseImg from 'components/LicenseImg';
import StateSelect from 'components/form/StateSelect';
import s from './s.module.less';
import { ILicenseData, LicenseData } from 'types/newUniprofile';
import { ELicenseType } from 'types/common';
import dayjs from 'dayjs';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import { isEqual } from 'lodash';

interface Props {
    initValue?: ILicenseData[];
    isEditing?: boolean;
}

const MalpracticeInsuranceCertificate = forwardRef((props: Props, ref) => {
    const { isEditing, initValue } = props;
    const [form] = Form.useForm<{ licenses: LicenseData[] }>();
    const prevInitValueRef = useRef<ILicenseData[]>();

    useEffect(() => {
        if (initValue?.length && !isEqual(prevInitValueRef.current, initValue)) {
            prevInitValueRef.current = initValue;
            form.setFieldsValue({
                licenses: initValue.map((item) => ({
                    ...item,
                    expireDate: item.expireDate ? dayjs(item.expireDate) : undefined,
                })),
            });
        }
    }, [initValue]);

    const getValues = async () => {
        const values = await form.validateFields();
        return values.licenses.map((item) => ({
            ...item,
            expireDate: item.expireDate ? item.expireDate.format('YYYY-MM-DD') : undefined,
            type: ELicenseType.CONTINUING_MEDICAL_EDUCATION_CERTIFICATES,
        }));
    };

    useImperativeHandle(ref, () => ({
        getValues,
    }));

    const renderEditing = () => {
        return (
            <div className={s.wrap}>
                <Form
                    form={form}
                    name="basic"
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    layout="vertical"
                >
                    <Form.List name="licenses" initialValue={[{}]}>
                        {(fields, { add, remove }) => {
                            const { length } = fields;
                            return (
                                <div id="cmecLicense">
                                    {fields.map((field, index) => {
                                        return (
                                            <Form.Item
                                                key={field.key}
                                                noStyle
                                            >
                                                <div className={s.subModule}>
                                                    <div className={s.title}>Continuing Medical Education Certificates {index + 1}</div>
                                                    <Divider style={{ margin: '6px 0 24px' }} />
                                                    <Form.Item
                                                        style={{ width: '320px' }}
                                                        label="License state"
                                                        name={[field.name, 'state']}
                                                        validateFirst
                                                        rules={[
                                                            { required: true, message: 'License state is required' },
                                                        ]}
                                                    >
                                                        <StateSelect />
                                                    </Form.Item>
                                                    <div className="flex gap-6">
                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="CME number"
                                                            name={[field.name, 'licenseNumber']}
                                                            validateFirst
                                                            rules={[
                                                                { required: true, message: 'CME License number is required' },
                                                            ]}
                                                        >
                                                            <Input placeholder="What is the CME license number?  " />
                                                        </Form.Item>
                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="Certificated title"
                                                            name={[field.name, 'certificatedTitle']}
                                                            rules={[{ required: true, message: 'Certificated title is required' }]}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </div>
                                                    <div className="flex gap-6">
                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="Class title"
                                                            name={[field.name, 'classTitle']}
                                                            rules={[{ required: true, message: 'Class title is required' }]}
                                                        >
                                                            <Input />
                                                        </Form.Item>

                                                        <Form.Item
                                                            style={{ width: '320px' }}
                                                            label="Hours completed"
                                                            name={[field.name, 'hourCompleted']}
                                                            rules={[{ required: true, message: 'Hours completed is required' }]}
                                                        >
                                                            <InputNumber controls={false} style={{ width: '100%' }} />
                                                        </Form.Item>
                                                    </div>
                                                    <Form.Item
                                                        style={{ width: '300px' }}
                                                        label="License copy"
                                                        name={[field.name, 'url']}
                                                        rules={[{ required: true, message: 'License copy is required' }]}
                                                    >
                                                        <FileUploadPrivate
                                                            uploadType="LICENSE"
                                                        />
                                                        {/* <FileUpload /> */}
                                                    </Form.Item>
                                                    {field.name !== 0 &&
                                                        <div className={s.removeWrap}>
                                                            <Button
                                                                onClick={() => remove(index)}
                                                                danger
                                                                size="small"
                                                            >Remove
                                                            </Button>
                                                        </div>}
                                                </div>
                                                {field.name === length - 1 &&
                                                    <div
                                                        onClick={() => {
                                                            add();
                                                            const wrap = document.getElementById('cmecLicense');
                                                            setTimeout(() => {
                                                                wrap?.parentElement?.parentElement?.scroll({
                                                                    top: 1000000,
                                                                    behavior: 'smooth',
                                                                });
                                                            }, 200);
                                                        }}
                                                        className={s.add}
                                                    >
                                                        + Add another CME certificates
                                                    </div>
                                                }
                                            </Form.Item>);
                                    })}
                                </div>);
                        }}
                    </Form.List>
                </Form>
            </div>
        );
    };

    const renderReview = () => {
        const licensesArray: ILicenseData[] = initValue?.filter((item: ILicenseData) => !!(item && item.licenseNumber)) ?? [];
        if (!licensesArray?.length) {
            return (
                <div className="mt-6">
                    <div className={s.title}>Continuing Medical Education Certificates</div>
                    <div className="mt-3">Not provided</div>
                </div>
            );
        }
        return (
            <div className="mt-6">
                <div className={s.title}>Continuing Medical Education Certificates</div>
                <Divider style={{ margin: '6px 0 24px' }} />
                {
                    licensesArray?.map((item: ILicenseData) => {
                        return (
                            <div className={s.subModule} key={item.licenseNumber}>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '320px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>CME state</div>
                                        <div className={s.value}>{item.state}</div>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '320px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>CME number</div>
                                        <div className={s.value}>{item.licenseNumber}</div>
                                    </div>
                                    <div
                                        style={{
                                            width: '320px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>Certificated title</div>
                                        <div className={s.value}>{item.certificatedTitle}</div>
                                    </div>
                                    <div
                                        style={{
                                            width: '320px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>Class title</div>
                                        <div className={s.value}>{item.classTitle}</div>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '320px',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>Hours completed</div>
                                        <div className={s.value}>{item.hourCompleted}</div>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div
                                        style={{
                                            width: '80%',
                                        }}
                                        className={s.displayItem}
                                    >
                                        <div className={s.label}>Uploaded license</div>
                                        <div className={s.value}>
                                            <LicenseImg src={item.fullUrl || item.url} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        );
    };

    return (
        <div className="mb-4">
            {isEditing ? renderEditing() : renderReview()}
        </div>
    );
});

export default MalpracticeInsuranceCertificate;
